<template>
  <div
    class="bg-white min-h-content max-h-content mt-16 fixed inset-y-0 flex-shrink-0 z-30 transition-all duration-300"
    :class="[
      isExpanded
        ? 'w-full left-0'
        : 'w-1/3 lg:w-1/4 xl:w-1/5 2xl:max-w-[15%] left-0'
    ]"
    v-bind="$attrs"
  >
    <div class="h-full flex flex-col">
      <!-- Header -->
      <div class="relative px-5 pt-5">
        <div
          class="flex items-center"
          :class="[isExpanded ? 'justify-between' : 'justify-center']"
        >
          <div class="flex justify-center items-center space-x-4">
            <img
              v-if="projectsStore.project?.company_id && companyLogo"
              :src="companyLogo.custom_properties.access_url"
              class="transition-[height] duration-75"
              :class="[isExpanded ? 'h-16' : 'h-12']"
            >

            <p class="text-center font-semibold text-xl">
              {{ isExpanded ? $t('Sidebar.header.projectDashboard') : $t('Sidebar.header.projects') }}
            </p>
          </div>

          <div
            class="hover:bg-gray-300 cursor-pointer p-1 rounded bg-gray-100 transition-transform"
            :class="[isExpanded ? '' : 'left-full absolute -translate-x-1/2 after:absolute after:-inset-3']"
            @click="toggleExpanded"
            @mouseover.prevent.stop=""
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" v-if="!isExpanded"
             
              class="w-5">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>

            <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 v-else-if="hasProjects"
             
              class="w-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
          </div>
        </div>

        <div
          style="--animate-duration: 0.1s;"
          class="flex flex-col"
        >
          <TransitionGroup
            enter-active-class="animate__animated animate__fadeIn"
            leave-active-class="animate__animated animate__fadeOut"
          >
            <template v-if="!hideProjects">
              <!-- Search input (only show when not expanded) -->
              <div
                v-if="!isExpanded"
                class="mt-2"
              >
                <AppInputIcon
                  v-model="projectSearch"
                  name="project_filter"
                  class="w-full"
                  :placeholder="$t('Sidebar.search.placeholder')"
                >
                  <template #icon>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>
                  </template>
                </AppInputIcon>
              </div>

              <div
                v-if="!isExpanded && archivedProjects.length > 0"
                class="mt-3 flex"
              >
                <button
                  type="button"
                  class="btn w-1/2 rounded-b-none"
                  :class="[showArchived ? 'btn--secondary-blue' : 'btn--primary-blue']"
                  @click="showArchived = false;"
                >
                  {{ $t('Sidebar.filter.live') }}
                </button>

                <button
                  type="button"
                  class="btn w-1/2 rounded-b-none"
                  :class="[showArchived ? 'btn--primary-blue' : 'btn--secondary-blue']"
                  @click="showArchived = true;"
                >
                  {{ $t('Sidebar.filter.archived') }}
                </button>
              </div>
            </template>
          </TransitionGroup>
        </div>
      </div>

      <!-- Content Area -->
      <div
        class="flex-1 flex flex-col overflow-hidden"
        style="--animate-duration: 0.1s;"
      >
        <Transition
          enter-active-class="animate__animated animate__fadeIn "
          leave-active-class="animate__animated animate__fadeOut"
          mode="out-in"
        >
          <!-- Project Dashboard (when expanded) -->
          <ProjectDashboard
            v-if="isExpanded && !hideProjects"
            :can-add-project="canAddProject"
            @expanded="handleProjectDashboardExpanded"
            @new-project="show = true"
          />

          <!-- Project List (when not expanded) -->
          <div
            v-else-if="!hideProjects"
            class="overflow-y-auto space-y-3 py-3 px-5"
          >
            <RouterLink
              v-for="project in listedProjects"
              :key="project.id"
              :to="{ name: 'ProjectSelector', params: { projectId: project.id } }"
              class="block text-sm"
            >
              <button
                type="button"
                class="btn w-full text-left"
                :class="[projectsStore.project?.id === project.id ? 'btn--primary-blue' : 'btn--secondary-blue']"
              >
                <p
                  class="text-xs font-semibold"
                  :class="[projectsStore.project?.id === project.id ? 'text-white' : 'text-gray-700']"
                >
                  {{ project.reference }}
                </p>
                {{ project.name }}
              </button>
            </RouterLink>
          </div>
        </Transition>
      </div>

      <!-- Footer (only show when not expanded) -->
      <div
        v-if="!isExpanded"
        class="flex-shrink-0 mt-auto"
      >
        <hr class="border border-gray-300">

        <div class="px-5 py-3">
          <button
            v-if="canAddProject"
            class="btn btn--secondary-blue w-full"
            type="button"
            @click="show = true"
          >
            {{ $t('Sidebar.button.addProject') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <Modal
    v-if="show"
    @close="show = false"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('Sidebar.modal.createProject') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <form @submit.prevent="createProject">
          <label>
            {{ $t('Sidebar.modal.name') }}
            <AppInput
              v-model="projectForm.form.name"
              type="text"
              name="name"
              class="form-input"
              :error="projectForm.getErrors('name')"
            />
          </label>

          <label v-if="companyProjects.length > 1">
            {{ $t('Sidebar.modal.copyUserRoles') }}
            <AppSelect
              v-model="projectForm.form.clone_project_id"
              name="clone_project_id"
              class="form-input pr-8"
              :options="companyProjects.map((project) => {
                return {
                  label: project.name,
                  value: project.id
                }
              })"
            />
          </label>

          <label v-if="availableCompanies.length > 1">
            {{ $t('Sidebar.modal.company') }}
            <AppSelect
              v-model="projectForm.form.company_id"
              name="company_id"
              class="form-input pr-8"
              :options="availableCompanies.map((permission) => {
                return {
                  label: permission.company.name,
                  value: permission.company.id
                }
              })"
            />
          </label>

          <label v-if="selectedCompany?.type === 'all'">
            {{ $t('Sidebar.modal.projectType') }}
            <AppSelect
              v-model="projectForm.form.type"
              name="type"
              class="form-input pr-8 capitalize"
              :options="['contractor', 'client']"
              :error="projectForm.getErrors('type')"
            />
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="show = false"
        >
          {{ $t('Sidebar.modal.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          @click="createProject"
        >
          {{ $t('Sidebar.modal.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import ProjectDashboard from '@/components/ProjectSelector/ProjectDashboard.vue';
import { useFormData } from '@/composables/useFormData';
import { Media } from '@/composables/useMediaCollection';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { falsey } from '@/helpers';
import { t } from '@/i18n';
import Permissions from '@/models/Permissions';
import User from '@/models/User';
import { useCompaniesStore } from '@/store/companies';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useRepo } from 'pinia-orm';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  open: boolean;
}>();

const emits = defineEmits<{
  (e: 'expanded', value: boolean);
}>();

const router = useRouter();
const projectsStore = useProjectsStore();
const companiesStore = useCompaniesStore();
const navStore = useNavStore();
const projectSearch = ref<string | undefined>();
const showArchived = ref(false);
const hideProjects = ref(false);
const isExpanded = computed(() => navStore.sidebarExpanded);

const hasProjects = computed(() => {
  return projectsStore.models.all().length > 0;
});

const projects = computed(() => {
  const projectsQuery = projectsStore.models.query();

  if(projectSearch.value) {
    const lowerCaseSearch = projectSearch.value.toLowerCase();

    projectsQuery
      .where((project) => {
        return project.name.toLowerCase().includes(lowerCaseSearch) ||
          project.reference?.toLowerCase().includes(lowerCaseSearch);
      });
  }

  return projectsQuery.whereNull('archived_at').get();
});

const archivedProjects = computed(() => {
  const projectsQuery = projectsStore.models.query();

  if(projectSearch.value) {
    const lowerCaseSearch = projectSearch.value.toLowerCase();

    projectsQuery
      .where((project) => {
        return project.name.toLowerCase().includes(lowerCaseSearch) ||
          project.reference?.toLowerCase().includes(lowerCaseSearch);
      });
  }

  return projectsQuery.whereNotNull('archived_at').get();
});

const listedProjects = computed(() => {
  if(showArchived.value) {
    return archivedProjects.value;
  }

  return projects.value;
});

const show = ref(false);

const projectForm = useFormData({
  name: '',
  company_id: undefined,
  clone_project_id: undefined,
  type: undefined,
});

const createProjectAction = useStoreApiAction(projectsStore.createCompanyProject);

const createProject = () => {
  projectForm.resetErrors();

  const hadProjectsBefore = hasProjects.value;

  createProjectAction.request(projectForm.form.company_id, projectForm.form).then((project) => {
    if(!hadProjectsBefore) {
      Swal.fire({
        icon: 'success',
        text: t('Sidebar.projectCreated'),
      });
    }

    router.push({ name: 'ProjectSelector', params: { projectId: project.id } });
    show.value = false;
    projectForm.resetData();
  }).catch((error) => {
    if(error?.status === 403) {
      Swal.fire({
        icon: 'error',
        text: t('Sidebar.error.maxProjectsReached'),
      });
    } else {
      projectForm.setErrors(error.data);
    }
  });
};

const userStore = useUserStore();

const availableCompanies = computed(() => {
  return useRepo(Permissions)
    .query()
    .where('user_id', (userStore.user as User)?.id)
    .where('company_id', (company_id: Id) => !!company_id)
    .with('company')
    .get();
});

watch([availableCompanies, show], (newValue) => {
  if(newValue[0]?.length === 1) {
    projectForm.form.company_id = newValue[0][0].company_id;
  }
}, { immediate: true });

const companyProjects = computed(() => {
  return projects.value.filter((project) => {
    return project.company_id === projectForm.form.company_id;
  });
});

const selectedCompany = computed(() => {
  return companiesStore.models.find(projectForm.form.company_id);
});

watch(() => projectForm.form.company_id, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    projectForm.resetData(['clone_project_id']);
  }
});

watch(() => projectsStore.project?.id, (newValue) => {
  if(newValue && projectsStore.models.find(newValue).archived_at) {
    showArchived.value = true;
  }
}, { immediate: true });

const showLogoAction = useStoreApiAction(companiesStore.showLogo);
const companyLogo = ref<undefined | Media>();

watch(() => [projectsStore.project?.company_id, projectsStore.project?.id], (newValue, oldValue) => {
  if(newValue && newValue[0] && (!oldValue || (newValue[0] !== oldValue[0] || newValue[1] !== oldValue[1]))) {
    showLogoAction.request(newValue[0], newValue[1]).then((logo: Record<string, Media>) => {
      for(const key in logo) {
        companyLogo.value = logo[key];
      }
    });
  } else if(newValue && falsey(newValue[0])) {
    companyLogo.value = undefined;
  }
}, { immediate: true });

const toggleExpanded = () => {
  hideProjects.value = true;

  nextTick(() => {
    setTimeout(() => {
      navStore.sidebarExpanded = !navStore.sidebarExpanded;
      hideProjects.value = false;

      emits('expanded', isExpanded.value);
    }, 150);
  });
};

const handleProjectDashboardExpanded = (expanded: boolean) => {
  navStore.sidebarExpanded = expanded;
  emits('expanded', expanded);
};

// Auto-collapse when a project is selected, but only if there's a projectId in the route
watch(() => projectsStore.project?.id, (newProjectId) => {
  if(newProjectId && isExpanded.value && router.currentRoute.value.params.projectId) {
    navStore.sidebarExpanded = false;
    emits('expanded', false);
  }
});

// Ensure sidebar is expanded when there's no projectId in the route
watch(() => router.currentRoute.value.params.projectId, (projectId) => {
  if(!projectId && !isExpanded.value) {
    navStore.sidebarExpanded = true;
    emits('expanded', true);
  }
}, { immediate: true });

const canAddProject = computed(() => {
  const projectBelongsToCompany = projectsStore.project?.company_id ?? false;
  const noProjectsExist = !hasProjects.value;
  let userHasCompanyAdminRole = false;

  if(availableCompanies.value.length > 0 || projectBelongsToCompany) {
    userHasCompanyAdminRole = userStore.isRoleForCompany(
      ['company_admin'],
      false,
      projectBelongsToCompany ? projectsStore.project?.company_id : availableCompanies.value[0].company_id,
    );
  }

  const companyCanCreateMoreProjects = companyProjects.value.length < selectedCompany.value?.max_projects;

  return !!(userHasCompanyAdminRole && (noProjectsExist || projectBelongsToCompany) && companyCanCreateMoreProjects);
});

defineOptions({
  inheritAttrs: false,
});
</script>

<style scoped></style>
